import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
    onEnter: true,
    onExit: false,
};

const useInViewObserver = ({
    root = DEFAULT.root,
    rootMargin = DEFAULT.rootMargin,
    threshold = DEFAULT.threshold,
    onEnter = DEFAULT.onEnter,
    onExit = DEFAULT.onExit,
} = {}) => {
    const [inView, setInView] = useState(!onEnter && onExit);
    const reference = useRef(null);

    const callback = useCallback(
        ([entry], observer) => {
            // We check all thresholds if they contain a intersecting value.
            const isIntersecting = observer.thresholds.every(threshold => entry.intersectionRatio > threshold);

            // If we have a change in the intersection observer, we act.
            if (inView !== isIntersecting) {
                // If we don't want to register object entering, then we unsubscribe and set state.
                if (!onEnter && isIntersecting) {
                    return observer.unobserve(reference.current);
                }

                // If we don't want to register object exiting, then we unsubscribe and set state.
                if (!onExit && !isIntersecting) {
                    return observer.unobserve(reference.current);
                }

                // If we listen to everything we just keep doing it.
                setInView(!inView);
            }
        },
        [inView, setInView, onEnter, onExit],
    );

    useEffect(() => {
        // Subscribe to observer with the IntersectionObserver API.
        const observer = new IntersectionObserver(callback, { root, rootMargin, threshold });

        // Need to declare a specific cleanup reference since the original one will disappear before unmount.
        const cleanupReference = reference.current;

        // Start a observation.
        reference.current && observer.observe(reference.current);

        // Cleanup observation.
        return () => cleanupReference && observer.unobserve(cleanupReference);
    }, [reference, root, rootMargin, threshold, callback]);

    return [reference, inView];
};

export default useInViewObserver;
